import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, catchError, of } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * RBAC permission check service. All requests are authenticated via the global
 * JwtInterceptor (withCredentials + token refresh). Uses backend endpoints:
 * - GET auth/rbac/check-permission/?permission=<code>
 * - POST auth/rbac/check-permissions-list/ { permissions: string[] }
 */

/** Response from check-permission (single). Adapt to backend shape if needed. */
export interface CheckPermissionResponse {
  allowed?: boolean;
  has_permission?: boolean;
}

/** Backend item: { code, has_permission } */
export interface PermissionItem {
  code: string;
  has_permission: boolean;
}

/** Response from check-permissions-list. Adapt to backend shape if needed. */
export interface CheckPermissionsListResponse {
  /** Backend format: array of { code, has_permission } */
  permissions?: PermissionItem[];
  allowed?: string[];
  denied?: string[];
  /** Alternative: map permission code -> allowed */
  [key: string]: boolean | string[] | PermissionItem[] | undefined;
}

@Injectable({
  providedIn: 'root'
})
export class PermissionCheckService {
  private readonly host = environment.apiUrlHost;
  private readonly base = `${this.host}/auth/rbac`;

  constructor(private http: HttpClient) {}

  /**
   * Check if the current user has a single permission.
   * Uses auth (JWT/cookies) via HttpClient interceptors.
   * @param permissionCode Permission code to check (e.g. 'permission.view_groups')
   * @returns Observable<boolean> true if allowed
   */
  checkPermission(permissionCode: string): Observable<boolean> {
    const params = new HttpParams().set('permission', permissionCode);
    return this.http
      .get<CheckPermissionResponse>(`${this.base}/check-permission/`, { params })
      .pipe(
        map((res) => this.normalizeSingleResponse(res)),
        catchError(() => of(false))
      );
  }

  /**
   * Check multiple permissions in one request.
   * Uses auth (JWT/cookies) via HttpClient interceptors.
   * @param permissionCodes List of permission codes
   * @returns Observable<Record<string, boolean>> map of permission code -> allowed
   */
  checkPermissionsList(permissionCodes: string[]): Observable<Record<string, boolean>> {
    if (!permissionCodes.length) {
      return of({});
    }
    return this.http
      .post<CheckPermissionsListResponse>(`${this.base}/check-permissions-list/`, {
        permissions: permissionCodes
      })
      .pipe(
        map((res) => this.normalizeListResponse(res, permissionCodes)),
        catchError(() => {
          const denied: Record<string, boolean> = {};
          permissionCodes.forEach((p) => (denied[p] = false));
          return of(denied);
        })
      );
  }

  /**
   * Check if the user has at least one of the given permissions.
   * Useful for route guards that require any of several permissions.
   */
  hasAnyPermission(permissionCodes: string[]): Observable<boolean> {
    if (!permissionCodes.length) return of(false);
    return this.checkPermissionsList(permissionCodes).pipe(
      map((result) => Object.values(result).some((v) => v === true))
    );
  }

  /**
   * Check if the user has all of the given permissions.
   */
  hasAllPermissions(permissionCodes: string[]): Observable<boolean> {
    if (!permissionCodes.length) return of(true);
    return this.checkPermissionsList(permissionCodes).pipe(
      map((result) => permissionCodes.every((code) => result[code] === true))
    );
  }

  private normalizeSingleResponse(res: CheckPermissionResponse): boolean {
    if (typeof res?.allowed === 'boolean') return res.allowed;
    if (typeof (res as any)?.has_permission === 'boolean') return (res as any).has_permission;
    return false;
  }

  private normalizeListResponse(
    res: CheckPermissionsListResponse,
    requested: string[]
  ): Record<string, boolean> {
    const out: Record<string, boolean> = {};
    // Backend format: { permissions: [ { code, has_permission } ] }
    if (Array.isArray(res.permissions)) {
      const byCode = new Map<string, boolean>();
      res.permissions.forEach((p) => byCode.set(p.code, p.has_permission === true));
      requested.forEach((code) => {
        out[code] = byCode.get(code) === true;
      });
      return out;
    }
    if (Array.isArray(res.allowed) && Array.isArray(res.denied)) {
      requested.forEach((code) => {
        out[code] = res.allowed!.includes(code);
      });
      return out;
    }
    requested.forEach((code) => {
      const val = (res as Record<string, boolean>)[code];
      out[code] = val === true;
    });
    return out;
  }
}
